// Inicio del shell «Orion». Todo número sale de un endpoint real (ver data.ts);
// si no hay datos se muestra 0 o un estado vacío que dice qué hacer.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  CalendarCheck,
  CalendarDays,
  ChevronDown,
  ExternalLink,
  Eye,
  FileCheck2,
  Globe,
  Lightbulb,
  Maximize2,
  MessageSquarePlus,
  Pencil,
  Plus,
  Radar,
  Send,
  Sparkles,
  UserPlus,
} from 'lucide-react';

import { ChatPane } from '@/components/assistant/ChatPane';
import { shellBrand } from '../brand';
import { useDashboardShell } from '@/context/DashboardShellContext';
import { useModules } from '@/context/ModulesContext';
import { decodeJwt } from '@/lib/api_superadmin';
import { canOpen, canReviewSite, type Access } from '../nav';
import {
  DAY_MS,
  dailySeries,
  isActiveAppt,
  makeWindow,
  periodCounts,
  useAgents,
  useAppointments,
  useLeadTimes,
  useMyName,
  usePostStatusCounts,
  usePrimaryDomain,
  usePublishedTimes,
  useReporteOrg,
  useSiteHero,
  useRevisions,
  type Appt,
  type PostStatusKey,
} from '../data';
import { Button, Card, EmptyState, ErrorNote, KpiCard, Loading } from '../ui';

const RANGES = [7, 30, 90] as const;
const RANGE_KEY = 'orion_home_range';

export const HOME_SUGGESTIONS = [
  '¿Cómo creo mi primer agente?',
  'Resume los leads de esta semana',
  '¿Qué cambios del sitio están pendientes?',
  'Propón una mejora para la portada del sitio',
];

function useRange(): [number, (n: number) => void] {
  const [r, setR] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem(RANGE_KEY));
      return (RANGES as readonly number[]).includes(v) ? v : 30;
    } catch {
      return 30;
    }
  });
  return [
    r,
    (n: number) => {
      setR(n);
      try {
        localStorage.setItem(RANGE_KEY, String(n));
      } catch {
        /* noop */
      }
    },
  ];
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

const fmtLongDate = new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const fmtShortDay = new Intl.DateTimeFormat('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
const fmtTime = new Intl.DateTimeFormat('es-MX', { hour: 'numeric', minute: '2-digit' });

export function OrionHome({ webChatHeaders }: { webChatHeaders: Record<string, string> }) {
  const navigate = useNavigate();
  const { availableModules, isAdmin, isDev, sovereign } = useModules();
  const access: Access = { availableModules, isAdmin, isDev, sovereign };
  const [range, setRange] = useRange();
  // La ventana se fija al montar / cambiar rango (no se recalcula en cada render).
  const w = useMemo(() => makeWindow(range), [range]);

  const userId = useMemo(() => {
    const t = localStorage.getItem('auth_token');
    const p: any = t ? decodeJwt(t) : null;
    return typeof p?.user_id === 'string' ? p.user_id : '';
  }, []);
  const myName = useMyName(userId);
  const firstName = (myName.data || '').split(/\s+/)[0];

  const can = (t: string) => canOpen(t, access);
  const open = (t: string) => navigate(`/m/${t}`);

  const agents = useAgents();
  const leads = useLeadTimes(w);
  const appts = useAppointments(w);
  const published = usePublishedTimes(w);
  const postCounts = usePostStatusCounts();
  const reporte = useReporteOrg();
  const pending = useRevisions('pending', 100, canReviewSite(access));

  // KPIs
  const activeAgents = agents.data ? agents.data.filter((a) => a.active !== false).length : null;
  const leadCounts = leads.data ? periodCounts(leads.data.times, w) : null;
  const leadSeries = leads.data ? dailySeries(leads.data.times, w) : null;

  const apptTimes = (appts.data?.items ?? []).filter(isActiveAppt).map((a) => a.start);
  const apptCounts = appts.data ? periodCounts(apptTimes, w) : null;
  const apptSeries = appts.data ? dailySeries(apptTimes, w) : null;

  const pubCounts = published.data ? periodCounts(published.data.times, w) : null;
  const pubSeries = published.data ? dailySeries(published.data.times, w) : null;

  const rep = reporte.data;
  const opportunities = rep?.kind === 'report' && rep.data.listo ? rep.data.matriz_oportunidades?.length ?? 0 : rep ? 0 : null;
  const oppSub =
    rep?.kind === 'none'
      ? 'Aún sin diagnóstico'
      : rep?.kind === 'forbidden'
        ? 'Diagnóstico no activado'
        : rep?.kind === 'report' && !rep.data.listo
          ? `Faltan ${rep.data.faltan_participantes} participantes`
          : rep?.kind === 'report'
            ? 'Del último diagnóstico'
            : undefined;

  const periodLabel = `últimos ${range} días`;
  const leadsTarget = can('crm/leads') ? 'crm/leads' : can('leads-import/crm') ? 'leads-import/crm' : null;

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      {/* Saludo */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-[30px]">
            {firstName ? `Hola, ${firstName}` : 'Hola'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {capitalize(fmtLongDate.format(new Date()))} · Esto es lo que pasa en tu organización.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="orion-range" className="text-sm text-muted-foreground">
            Periodo
          </label>
          <select
            id="orion-range"
            value={range}
            onChange={(e) => setRange(Number(e.target.value))}
            className="rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground"
          >
            {RANGES.map((r) => (
              <option key={r} value={r}>
                Últimos {r} días
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* KPIs */}
      <section aria-label="Indicadores" className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5 [&>*:last-child]:col-span-2 lg:[&>*:last-child]:col-span-1">
        <KpiCard
          icon={<Bot size={20} />}
          label="Agentes activos"
          tone="violet"
          value={agents.isError ? null : activeAgents}
          loading={agents.isLoading}
          sub={agents.data ? `de ${agents.data.length} creados` : agents.isError ? 'Sin acceso' : undefined}
          onOpen={can('orion/agentes') ? () => open('orion/agentes') : undefined}
          href="/m/orion/agentes"
        />
        <KpiCard
          icon={<UserPlus size={20} />}
          label="Leads generados"
          tone="indigo"
          value={leads.isError ? null : leadCounts?.current ?? null}
          loading={leads.isLoading}
          delta={leadCounts}
          spark={leadSeries}
          sub={leads.isError ? 'Sin acceso' : leads.data?.truncated ? 'Muestra parcial' : periodLabel}
          onOpen={leadsTarget ? () => open(leadsTarget) : undefined}
          href={leadsTarget ? `/m/${leadsTarget}` : undefined}
        />
        <KpiCard
          icon={<CalendarCheck size={20} />}
          label="Citas agendadas"
          tone="blue"
          value={appts.isError ? null : apptCounts?.current ?? null}
          loading={appts.isLoading}
          delta={apptCounts}
          spark={apptSeries}
          sub={periodLabel}
          onOpen={can('orion/calendario') ? () => open('orion/calendario') : undefined}
          href="/m/orion/calendario"
        />
        <KpiCard
          icon={<Send size={20} />}
          label="Contenido publicado"
          tone="magenta"
          value={published.isError ? null : pubCounts?.current ?? null}
          loading={published.isLoading}
          delta={pubCounts}
          spark={pubSeries}
          sub={published.isError ? 'Sin acceso' : published.data?.truncated ? 'Muestra parcial' : periodLabel}
          onOpen={can('mkt-meta/social-publish') ? () => open('mkt-meta/social-publish') : undefined}
          href="/m/mkt-meta/social-publish"
        />
        <KpiCard
          icon={<Lightbulb size={20} />}
          label="Oportunidades detectadas"
          tone="purple"
          value={reporte.isError ? null : opportunities}
          loading={reporte.isLoading}
          sub={reporte.isError ? 'No se pudo leer' : oppSub}
          onOpen={can('org-scan/entrevista') ? () => open('org-scan/entrevista') : undefined}
          href="/m/org-scan/entrevista"
        />
      </section>

      {/* Asistente + acciones + citas */}
      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-12">
        <AssistantPanel webChatHeaders={webChatHeaders} className="xl:col-span-7" />

        <div className="flex min-w-0 flex-col gap-4 xl:col-span-5">
          <Card title="Acciones rápidas" titleId="qa-title">
            <ul className="grid auto-rows-fr grid-cols-1 gap-2.5 sm:grid-cols-2">
              <QuickAction
                icon={<FileCheck2 size={18} />}
                title="Aprobar contenido pendiente"
                tone="magenta"
                count={postCounts.data?.draft}
                loading={postCounts.isLoading}
                detail={postCounts.isError ? 'Sin acceso al contenido' : postCounts.data?.draft ? 'borradores por revisar' : 'Nada por aprobar'}
                onOpen={can('mkt-meta/approve') ? () => open('mkt-meta/approve') : undefined}
              />
              <QuickAction
                icon={<Globe size={18} />}
                title="Revisar sitio web"
                tone="blue"
                count={pending.data?.length}
                loading={pending.isLoading && pending.fetchStatus !== 'idle'}
                detail={
                  !canReviewSite(access)
                    ? 'Solo para administradores'
                    : pending.isError
                      ? 'No se pudo leer'
                      : pending.data?.length
                        ? 'cambios pendientes de aprobar'
                        : 'Sin cambios pendientes'
                }
                onOpen={canReviewSite(access) ? () => open('orion/cambios-sitio') : undefined}
              />
              <QuickAction
                icon={<Radar size={18} />}
                title="Diagnóstico organizacional"
                tone="purple"
                count={rep?.kind === 'report' ? rep.data.participantes : undefined}
                loading={reporte.isLoading}
                detail={
                  rep?.kind === 'report'
                    ? rep.data.listo
                      ? 'participantes · resultado listo'
                      : `participantes · faltan ${rep.data.faltan_participantes}`
                    : rep?.kind === 'none'
                      ? 'Aún no empieza'
                      : 'No activado para tu organización'
                }
                onOpen={can('org-scan/entrevista') ? () => open('org-scan/entrevista') : undefined}
              />
              <QuickAction
                icon={<Plus size={18} />}
                title="Crear nuevo agente"
                tone="violet"
                count={agents.data?.length}
                loading={agents.isLoading}
                detail={agents.data?.length ? 'agentes creados' : 'Empieza con tu primer agente'}
                onOpen={can('orion/agentes') ? () => navigate('/m/orion/agentes?nuevo=1') : undefined}
              />
            </ul>
          </Card>

          <UpcomingAppointments
            loading={appts.isLoading}
            error={appts.isError}
            items={(appts.data?.items ?? []).filter((a) => isActiveAppt(a) && a.start >= Date.now()).slice(0, 4)}
            onOpenAll={can('orion/calendario') ? () => open('orion/calendario') : undefined}
          />
        </div>
      </div>

      {/* Gráficas + sitio */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-12">
        <LeadsChart
          className="lg:col-span-2 xl:col-span-4"
          loading={leads.isLoading}
          error={leads.isError}
          series={leadSeries}
          from={w.from}
          range={range}
          onOpen={leadsTarget ? () => open(leadsTarget) : undefined}
        />
        <ContentDonut
          className="xl:col-span-4"
          loading={postCounts.isLoading}
          error={postCounts.isError}
          counts={postCounts.data ?? null}
          onOpen={can('mkt-meta/social-publish') ? () => open('mkt-meta/social-publish') : undefined}
        />
        <SitePreview
          className="xl:col-span-4"
          onEdit={
            can('site/editor') && can('orion/editor-sitio')
              ? () => open('orion/editor-sitio')
              : can('site/builder') ? () => open('site/builder') : undefined
          }
        />
      </div>
    </div>
  );
}

// ── Asistente ─────────────────────────────────────────────────

const SUGGESTION_ITEMS = [
  { text: '¿Cómo creo mi primer agente?', icon: <Bot size={16} /> },
  { text: 'Resume los leads de esta semana', icon: <UserPlus size={16} /> },
  { text: '¿Qué cambios del sitio están pendientes?', icon: <Globe size={16} /> },
  { text: 'Propón una mejora para la portada del sitio', icon: <Sparkles size={16} /> },
];

function AssistantPanel({ webChatHeaders, className = '' }: { webChatHeaders: Record<string, string>; className?: string }) {
  const brand = shellBrand(useDashboardShell().config);
  const navigate = useNavigate();
  const [nonce, setNonce] = useState(0);
  const agents = useAgents();
  const [agentId, setAgentId] = useState('');
  const list = (agents.data ?? []).filter((a) => a.active !== false);
  return (
    <section aria-labelledby="assistant-title" className={`orion-card flex min-w-0 flex-col overflow-hidden rounded-[20px] border border-border bg-card ${className}`}>
      <header className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
          <img src={brand.symbol} alt="" className="h-7 w-7" />
        </span>
        <div className="min-w-[55%] flex-1 sm:min-w-0">
          <h2 id="assistant-title" className="text-base font-semibold tracking-tight text-foreground">Asistente de {brand.assistantName}</h2>
          <p className="text-xs text-muted-foreground">Trabaja con la información de tu organización</p>
        </div>
        <label className="relative order-last inline-flex items-center sm:order-none">
          <span className="sr-only">Agente que responde</span>
          <Bot size={14} aria-hidden className="pointer-events-none absolute left-2.5 text-primary" />
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            disabled={list.length === 0}
            title={list.length === 0 ? 'Crea un agente para elegirlo aquí' : 'Elige qué agente responde'}
            className="max-w-[180px] appearance-none truncate rounded-full border border-border bg-muted py-1.5 pl-7 pr-7 text-xs font-medium text-foreground disabled:opacity-80"
          >
            <option value="">Asistente general</option>
            {list.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <ChevronDown size={13} aria-hidden className="pointer-events-none absolute right-2.5 text-muted-foreground" />
        </label>
        <div className="flex items-center gap-1">
          <Button variant="ghost" onClick={() => setNonce(Date.now())} className="!px-2.5 !py-1.5">
            <MessageSquarePlus size={15} aria-hidden /> <span className="hidden sm:inline">Nuevo chat</span>
            <span className="sr-only sm:hidden">Nuevo chat</span>
          </Button>
          <Button variant="ghost" onClick={() => navigate('/m/orion/asistente')} aria-label="Abrir el asistente en pantalla completa" className="!px-2">
            <Maximize2 size={15} aria-hidden />
          </Button>
        </div>
      </header>
      <div className="flex h-[480px] flex-1 flex-col xl:h-auto xl:min-h-[480px] [&>div]:flex-1">
        <ChatPane
          key={nonce}
          startFresh={nonce > 0}
          webChatHeaders={webChatHeaders}
          hideThinking
          assistantAvatarUrl={brand.symbol}
          assistantId={agentId}
          hideAgentSelector
          modelPicker
          mentions
          suggestionItems={SUGGESTION_ITEMS}
          emptyTitle="¿En qué te ayudo hoy?"
          emptySubtitle={brand.subtitle}
        />
      </div>
    </section>
  );
}

// ── Acciones rápidas ──────────────────────────────────────────

function QuickAction({
  icon,
  tone = 'violet',
  title,
  count,
  detail,
  loading,
  onOpen,
}: {
  icon: React.ReactNode;
  tone?: 'violet' | 'indigo' | 'blue' | 'magenta' | 'purple';
  title: string;
  count?: number;
  detail: string;
  loading?: boolean;
  onOpen?: () => void;
}) {
  const inner = (
    <>
      <span className={`orion-tone tone-${tone} flex h-10 w-10 shrink-0 items-center justify-center rounded-xl`} aria-hidden>
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold leading-snug text-foreground">{title}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {loading ? 'Cargando…' : (
            <>
              {typeof count === 'number' && count > 0 && <strong className="font-semibold text-foreground tabular-nums">{count} </strong>}
              {detail}
            </>
          )}
        </span>
      </span>
      {onOpen && <ArrowRight size={15} aria-hidden className="mt-1 shrink-0 text-muted-foreground" />}
    </>
  );
  return (
    <li>
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className="flex h-full min-h-[84px] w-full items-start gap-3 rounded-2xl border border-border bg-card p-3.5 text-left transition-colors hover:border-primary/40 hover:bg-muted/50"
        >
          {inner}
        </button>
      ) : (
        <div className="flex h-full min-h-[84px] items-start gap-3 rounded-2xl border border-dashed border-border p-3.5" aria-disabled="true">
          {inner}
        </div>
      )}
    </li>
  );
}

// ── Próximas citas ────────────────────────────────────────────

export function ApptRow({ a }: { a: Appt }) {
  const d = new Date(a.start);
  return (
    <li className="flex items-center gap-3 rounded-xl px-1 py-2">
      <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl bg-muted text-foreground">
        <span className="text-[10px] font-medium uppercase leading-none text-muted-foreground">
          {d.toLocaleDateString('es-MX', { month: 'short' }).replace('.', '')}
        </span>
        <span className="text-base font-semibold leading-tight tabular-nums">{d.getDate()}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{a.title}</p>
        <p className="text-xs text-muted-foreground">
          {capitalize(d.toLocaleDateString('es-MX', { weekday: 'long' }))} · {fmtTime.format(d)} ·{' '}
          {a.source === 'agente' ? 'Agenda del agente' : 'Reservas'}
        </p>
      </div>
      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground">
        {statusLabel(a.status)}
      </span>
    </li>
  );
}

function statusLabel(s: string): string {
  const k = String(s || '').toLowerCase();
  if (k === 'confirmed') return 'Confirmada';
  if (k === 'pending' || k === 'pending_confirmation') return 'Por confirmar';
  if (k === 'completed' || k === 'done') return 'Realizada';
  return s || 'Programada';
}

function UpcomingAppointments({
  items,
  loading,
  error,
  onOpenAll,
}: {
  items: Appt[];
  loading: boolean;
  error: boolean;
  onOpenAll?: () => void;
}) {
  return (
    <Card
      title="Próximas citas"
      titleId="appts-title"
      action={
        onOpenAll && (
          <button type="button" onClick={onOpenAll} className="text-sm font-medium text-primary hover:underline">
            Ver calendario
          </button>
        )
      }
    >
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorNote>No se pudieron leer las citas.</ErrorNote>
      ) : items.length === 0 ? (
        <EmptyState icon={<CalendarDays size={18} />} title="Aún no hay citas próximas">
          Cuando tu agente agende citas o recibas reservas, aparecerán aquí. Si usas otra agenda, conéctala desde Configuración.
        </EmptyState>
      ) : (
        <ul className="-my-1 divide-y divide-border">
          {items.map((a) => (
            <ApptRow key={a.id} a={a} />
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── Rendimiento de leads ─────────────────────────────────────

function ChartTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload as { label: string; value: number };
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg">
      <p className="font-medium">{p.label}</p>
      <p className="text-muted-foreground">
        <span className="font-semibold text-foreground tabular-nums">{p.value}</span> {p.value === 1 ? 'lead' : 'leads'}
      </p>
    </div>
  );
}

function LeadsChart({
  series,
  from,
  range,
  loading,
  error,
  onOpen,
  className = '',
}: {
  series: number[] | null;
  from: number;
  range: number;
  loading: boolean;
  error: boolean;
  onOpen?: () => void;
  className?: string;
}) {
  const data = (series ?? []).map((v, i) => {
    const d = new Date(from + i * DAY_MS + DAY_MS / 2);
    return { idx: i, day: d.getDate(), label: capitalize(fmtShortDay.format(d)), value: v };
  });
  const total = data.reduce((s, d) => s + d.value, 0);
  const every = range <= 7 ? 1 : range <= 30 ? 5 : 15;
  return (
    <Card
      title="Rendimiento de leads"
      titleId="leads-chart-title"
      className={className}
      action={
        onOpen && (
          <button type="button" onClick={onOpen} className="text-sm font-medium text-primary hover:underline">
            Ver leads
          </button>
        )
      }
    >
      <p className="-mt-1 mb-3 text-xs text-muted-foreground">
        Leads nuevos por día · últimos {range} días · <strong className="font-semibold text-foreground tabular-nums">{total}</strong> en total
      </p>
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorNote>No se pudieron leer los leads.</ErrorNote>
      ) : total === 0 ? (
        <div className="h-[220px]">
          <EmptyState icon={<UserPlus size={18} />} title="Aún no hay leads en este periodo">
            Cuando tu agente o tus formularios registren prospectos, verás aquí cuántos llegan cada día.
          </EmptyState>
        </div>
      ) : (
        <div className="h-[220px]" role="img" aria-label={`Leads por día en los últimos ${range} días: ${total} en total`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }} barCategoryGap={2}>
              <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
              <XAxis
                dataKey="idx"
                tickLine={false}
                axisLine={false}
                interval={0}
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(i: number) => (i % every === 0 || i === data.length - 1 ? String(data[i]?.day ?? '') : '')}
              />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={40} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'hsl(var(--muted))' }} />
              <Bar dataKey="value" fill="var(--chart-bar)" radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

// ── Estado de contenido ──────────────────────────────────────

const CONTENT_META: Array<{ key: PostStatusKey; label: string; color: string }> = [
  { key: 'published', label: 'Publicados', color: 'var(--chart-published)' },
  { key: 'scheduled', label: 'Programados', color: 'var(--chart-scheduled)' },
  { key: 'draft', label: 'Borradores', color: 'var(--chart-draft)' },
  { key: 'failed', label: 'Fallidos', color: 'var(--chart-failed)' },
];

function ContentDonut({
  counts,
  loading,
  error,
  onOpen,
  className = '',
}: {
  counts: Record<PostStatusKey, number> | null;
  loading: boolean;
  error: boolean;
  onOpen?: () => void;
  className?: string;
}) {
  const total = counts ? CONTENT_META.reduce((s, m) => s + (counts[m.key] || 0), 0) : 0;
  const data = CONTENT_META.map((m) => ({ ...m, value: counts?.[m.key] ?? 0 })).filter((d) => d.value > 0);
  return (
    <Card
      title="Estado de contenido"
      titleId="content-title"
      className={className}
      action={
        onOpen && (
          <button type="button" onClick={onOpen} className="text-sm font-medium text-primary hover:underline">
            Ver contenido
          </button>
        )
      }
    >
      <p className="-mt-1 mb-3 text-xs text-muted-foreground">Publicaciones para redes, todas las fechas</p>
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorNote>No se pudo leer el contenido.</ErrorNote>
      ) : total === 0 ? (
        <div className="h-[220px]">
          <EmptyState icon={<FileCheck2 size={18} />} title="Aún no hay publicaciones">
            Cuando crees o programes contenido para tus redes, verás aquí cuántas piezas hay en cada estado.
          </EmptyState>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4 sm:flex-row xl:flex-col 2xl:flex-row">
          <div className="relative h-[160px] w-[160px] shrink-0" role="img" aria-label={`Contenido: ${CONTENT_META.map((m) => `${m.label} ${counts?.[m.key] ?? 0}`).join(', ')}`}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="label" innerRadius={52} outerRadius={76} paddingAngle={data.length > 1 ? 2 : 0} stroke="hsl(var(--card))" strokeWidth={2} isAnimationActive={false}>
                  {data.map((d) => (
                    <Cell key={d.key} fill={d.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-semibold tabular-nums text-foreground">{total}</span>
              <span className="text-[11px] text-muted-foreground">en total</span>
            </div>
          </div>
          <ul className="w-full space-y-1.5 text-sm">
            {CONTENT_META.map((m) => {
              const v = counts?.[m.key] ?? 0;
              return (
                <li key={m.key} className="flex items-center gap-2">
                  <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: m.color }} />
                  <span className="flex flex-1 items-center gap-1 text-foreground">
                    {m.key === 'failed' && v > 0 && <AlertTriangle size={13} aria-hidden className="text-destructive" />}
                    {m.label}
                  </span>
                  <span className="tabular-nums font-semibold text-foreground">{v}</span>
                  <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">{total ? Math.round((v / total) * 100) : 0}%</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}

// ── Vista previa del sitio ───────────────────────────────────

function SitePreview({ onEdit, className = '' }: { onEdit?: () => void; className?: string }) {
  const domain = usePrimaryDomain();
  // Póster = imagen y título REALES del hero de la portada (site-config). El
  // sitio vivo (iframe) solo carga con «Ver en vivo», para no sumar una visita
  // a tus métricas cada vez que abres el panel.
  const hero = useSiteHero();
  const [live, setLive] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0.3);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setScale(el.clientWidth / 1280));
    ro.observe(el);
    setScale(el.clientWidth / 1280);
    return () => ro.disconnect();
  }, [live, domain.data]);

  const url = domain.data ? `https://${domain.data}` : null;

  return (
    <Card
      title="Vista previa del sitio web"
      titleId="site-title"
      className={className}
      action={
        url && (
          <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
            Abrir <ExternalLink size={13} aria-hidden />
            <span className="sr-only">(se abre en otra pestaña)</span>
          </a>
        )
      }
    >
      {domain.isLoading ? (
        <Loading />
      ) : !url ? (
        <div className="h-[220px]">
          <EmptyState icon={<Globe size={18} />} title="Tu organización aún no tiene sitio publicado">
            Cuando tengas un dominio activo, verás aquí cómo se ve tu sitio.
          </EmptyState>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-2xl border border-border bg-muted">
            <div className="flex items-center gap-1.5 border-b border-border bg-card px-3 py-2">
              <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-muted-foreground/25" />
              <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-muted-foreground/25" />
              <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-muted-foreground/25" />
              <span className="ml-2 truncate rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">{domain.data}</span>
            </div>
            <div ref={boxRef} className="relative aspect-[16/10] w-full overflow-hidden">
              {live ? (
                <iframe
                  src={url}
                  title={`Sitio en vivo: ${domain.data}`}
                  tabIndex={-1}
                  sandbox="allow-scripts allow-same-origin"
                  className="pointer-events-none absolute left-0 top-0 origin-top-left border-0 bg-card"
                  style={{ width: 1280, height: 800, transform: `scale(${scale})` }}
                />
              ) : (
                <div className="orion-site-poster absolute inset-0">
                  {hero.data?.image && (
                    <img src={hero.data.image} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                  )}
                  <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10" />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <p className="line-clamp-3 text-[15px] font-semibold leading-snug text-[color:var(--on-brand)] drop-shadow">
                      {hero.data?.title || domain.data}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {onEdit && (
              <Button variant="brand" onClick={onEdit}>
                <Pencil size={14} aria-hidden /> Editar sitio
              </Button>
            )}
            {!live && (
              <Button variant="secondary" onClick={() => setLive(true)}>
                <Eye size={14} aria-hidden /> Ver en vivo
              </Button>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

export default OrionHome;
